import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../../hooks/redux';
import { answerQuestion } from '../../../../services/AISystemsService';
import {
  decrementAnswerRequestsInFlight,
  getCurrentStepId,
  incrementAnswerRequestsInFlight,
  setDisableNextStep,
  setStepType,
  setNextQuestion,
  setSelectedEntity,
  updateStepBasedOnBackend,
  setIsLoading,
  setPreviousQuestions,
  setQuestionnaireServerErrorStatus,
  clearQuestionnaireServerError
} from '../../../../store/slices/stepperSlice';
import { Entity } from '../../../../types/Shared.types';
import Banner from '../../../ui/Banner/Banner';
import { TextButton } from '../../../ui/Button/Buttons';
import CheckboxGroup from '../../../ui/Checkbox/CheckboxGroup';
import RadioButtonGroup from '../../../ui/RadioButton/RadioButtonGroup';
import { QuestionLayoutProps } from './QuestionLayout.types';
import Loader from '../../../ui/Loader/Loader';
import { formatSourceElement } from '../../../../utils/Functions.utils';
import {
  getApiErrorStatus,
  isServerErrorStatus
} from '../../../../utils/apiError';

export function QuestionLayout({
  question,
  disabled = false,
  entity,
  onAnswerStatusChange
}: QuestionLayoutProps) {
  const dispatch = useDispatch();
  const { selectedEntity, questionnaireId, showPreStep, steps, isStepBack } =
    useAppSelector((state) => state.stepper);

  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [selectedSector, setSelectedSector] = useState<Entity | string>('');
  const [showDefinitions, setShowDefinitions] = useState(false);
  const [loadingAnswer, setLoadingAnswer] = useState(false);

  const currentStepId = getCurrentStepId(steps);

  // solution for now, backend should return with element with class
  const formattedSource = formatSourceElement(
    question.source,
    'underline-custom'
  );

  const handleChannelChange = (value: string, checked: boolean) => {
    if (disabled) return;

    if (value === 'none' && checked) {
      setSelectedChannels(['none']);
    } else if (checked) {
      setSelectedChannels((prev) => [...prev, value]);
    } else {
      setSelectedChannels((prev) => prev.filter((v) => v !== value));
    }
  };

  const getNextQuestion = async () => {
    try {
      if (!questionnaireId || showPreStep) return;

      const answerValue =
        question.type === 'checkbox' ? selectedChannels : [selectedSector];

      // if theres no answer dont call api(logic for now)
      if (!answerValue.length || answerValue[0] === '') return;
      dispatch(setDisableNextStep(true));
      setLoadingAnswer(true);
      dispatch(setIsLoading(true));
      dispatch(clearQuestionnaireServerError());
      dispatch(incrementAnswerRequestsInFlight());
      const response = await answerQuestion(questionnaireId, {
        questionId: question.id,
        answer: answerValue
      });

      if (!response) return;

      dispatch(setStepType(response.type));

      if (!response || response.type !== 'question') {
        dispatch(setPreviousQuestions([]));
        return;
      }

      dispatch(setNextQuestion(response));
      dispatch(updateStepBasedOnBackend(response.question.step));

      if (onAnswerStatusChange) onAnswerStatusChange(true);
    } catch (error: any) {
      const status = getApiErrorStatus(error);
      if (isServerErrorStatus(status)) {
        dispatch(setQuestionnaireServerErrorStatus(status));
      }
      console.error('Failed to answer on question');
    } finally {
      dispatch(decrementAnswerRequestsInFlight());
      dispatch(setDisableNextStep(false));
      dispatch(setIsLoading(false));
      setLoadingAnswer(false);
    }
  };

  useEffect(() => {
    const hasAnswer =
      (question.type === 'checkbox' && selectedChannels.length > 0) ||
      (question.type === 'radio' && selectedSector.trim() !== '');

    if (onAnswerStatusChange) onAnswerStatusChange(hasAnswer);
  }, [selectedChannels, selectedSector]);

  // dont call api if non option is selected
  useEffect(() => {
    if (disabled || !questionnaireId) return;

    if (selectedChannels.length === 0 && selectedSector.trim() === '') {
      dispatch(setDisableNextStep(true));
      dispatch(setNextQuestion(null));
    } else {
      if (currentStepId === 'STEP3') dispatch(setDisableNextStep(false)); // for now, should use different logic later

      dispatch(setDisableNextStep(false));
      getNextQuestion();
    }
  }, [selectedChannels, selectedSector, questionnaireId]);

  // select entitiy on pre-step
  useEffect(() => {
    dispatch(setSelectedEntity(selectedSector as Entity));
  }, [selectedEntity, selectedSector]);

  useEffect(() => {
    if (question.selected) {
      if (question.type === 'checkbox') {
        setSelectedChannels(
          Array.isArray(question.selected) ? question.selected : []
        );
      } else if (
        question.type === 'radio' &&
        typeof question.selected === 'string'
      ) {
        setSelectedSector(question.selected);
      }
    }
  }, [question]);

  useEffect(() => {
    if (!showPreStep) return;

    if (entity) setSelectedSector(entity);
  }, [entity, showPreStep]);

  return (
    <div
      className={`flex flex-col items-start gap-4 p-4 border border-gray-100 rounded-lg ${
        showPreStep ? '' : 'mx-12'
      }`}
    >
      <span className="text-zinc-900 font-medium text-left">
        {question.title}
      </span>

      {/* Display component based on question type */}
      {question.type === 'checkbox' && (
        <CheckboxGroup
          options={question.options}
          selectedValues={selectedChannels}
          onChange={handleChannelChange}
          disableCheckbox={selectedChannels.includes('none')}
          disableAllCheckboxes={disabled || loadingAnswer} // need test
        />
      )}
      {question.type === 'radio' && (
        <RadioButtonGroup
          name={question.title}
          onChange={setSelectedSector}
          selectedValue={selectedSector}
          options={question.options}
        />
      )}

      {/* Banner */}
      {question.hint && (
        <Banner
          html={question.hint}
          backgroundColorClass="bg-blue-50"
          textColorClass="text-purple-900"
          titleColorClass="text-purple-900"
          className="w-full"
        />
      )}

      {question.definitions && (
        <div className="flex flex-col gap-5 items-start">
          <TextButton
            onClick={() => setShowDefinitions(!showDefinitions)}
            className="text-xs"
          >
            {showDefinitions ? 'Hide Definitions' : 'Show Definitions'}
          </TextButton>
          {showDefinitions && (
            <div className="flex flex-col gap-4 bg-gray-50 rounded-lg p-4 text-left">
              {question.definitions.map((definition) => (
                <div
                  dangerouslySetInnerHTML={{ __html: definition }}
                  className="text-zinc-950"
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Source */}
      <div className="flex gap-0.5 justify-end relative w-full text-sm text-right text-gray-550">
        <span className="font-semibold">Source:</span>
        <div dangerouslySetInnerHTML={{ __html: formattedSource }} />
        {loadingAnswer && !isStepBack && (
          <div className="absolute left-0 top-0">
            <Loader width="20" height="20" />
          </div>
        )}
      </div>
    </div>
  );
}
